/**
 * The pages that make it read as a company that exists.
 *
 * They live with `landing` rather than `gtm` because that is the surface they belong to: a
 * visitor reaches them from the footer of the marketing site, not from a tool. Splitting them out
 * of the old shared `content.tsx` is what let landing and gtm become two builds without either
 * importing the other — the rule this tree now keeps is that an app owns its pages outright.
 */
import { Contact, Shell } from "@agitome/ui-shared"

function Legal({ title, eyebrow, children }: { title: string; eyebrow: string; children: React.ReactNode }) {
  return (
    <Shell>
      <div className="band band--bone">
        <div className="wrap wrap--narrow">
          <p className="eyebrow">{eyebrow}</p>
          <h1 className="display mt-4 balance">{title}</h1>
          {children}
        </div>
      </div>
    </Shell>
  )
}

export function Faq() {
  const qs: [string, string][] = [
    ["Is a report the same as a finding?", "No, and this is the distinction the whole product rests on. A report is something a stranger filed. It is published, searchable and shareable, and it moves no agent's score. Only an incident adjudicated against a signed loss event counts as evidence."],
    ["Can I get an incident about my product taken down?", "No — but you can dispute it, and a dispute from a verified owner takes it out of every published statistic while it runs. It stays visible, because a registry that can make entries disappear is one whose absences mean nothing. If we decide against you, you can still attach a permanent statement we never remove."],
    ["What happens if you do not decide in time?", "The dispute resolves in your favour automatically at 30 days, or 45 if we recorded an extension. The party that failed to decide is agitome, so the cost lands here."],
    ["Why is there no incident rate?", "A rate needs a denominator — how many agent deployments exist — and nobody publishes one. Any rate you see quoted for AI agents was produced by inventing that number."],
    ["Why is there no price on the pricing page?", "Because we do not have the loss data to price against, and a price implies we do. Design partners get a number in writing; it is just not one we will publish and then walk back."],
    ["Do you sell the incident data?", "No. The registry is free to read and stays that way. What is sold is enforcement — the gate in your pipeline, alerts when a subject moves, an adjudication SLA."],
    ["What do you do with my email address?", "Record it, so we can tell you when the thing you were looking at changes. Nothing sends yet — that is not built — and every page with a field has an unsubscribe link beside it that needs no login."],
    ["Is 'insufficient' a good sign?", "No. It is the absence of an assessment — too few counted reports to say anything — and it is styled apart from the tier ladder precisely so no sort order can present it as the safe end."],
  ]
  return (
    <Legal eyebrow="Questions" title="The awkward ones, in the open.">
      <div className="mt-8">
        {qs.map(([q, a]) => (
          <details className="faq-item" key={q}>
            <summary>{q}</summary>
            <p>{a}</p>
          </details>
        ))}
      </div>
    </Legal>
  )
}

export function Privacy() {
  return (
    <Legal eyebrow="Privacy" title="What we hold, and what we refuse to.">
      <p className="lede mt-6">
        agitome is a registry of reports about products. It is not a registry of people, and the
        design keeps it that way.
      </p>
      <dl className="spec mt-8">
        <div><dt>Incident reports</dt><dd>Public, and about a named product. They are never attributed to the reporter's address publicly.</dd></div>
        <div><dt>Email addresses</dt><dd>Recorded when you give one, so we can tell you when the thing you asked about changes. Never published, never sold. Removed immediately at /unsubscribe, with no login and no confirmation email.</dd></div>
        <div><dt>Visitors</dt><dd>Growth is measured against an HMAC of the client address with a secret we hold — so two visits can be told apart and neither can be traced back. The address itself is never stored.</dd></div>
        <div><dt>Tenant evidence</dt><dd>Signed loss events belong to the tenant that emitted them, are never cross-tenant readable, and are deletable on request. Loss amounts and event ids never appear on a public page.</dd></div>
        <div><dt>What we do not do</dt><dd>No advertising trackers, no third-party analytics, no selling anything to anyone.</dd></div>
      </dl>
      <p className="serif mt-8" style={{ fontSize: "var(--t-h3)" }}>
        A registry asking people to file reports about their employer has to be worth trusting with
        the address they used to do it.
      </p>
    </Legal>
  )
}

export function Terms() {
  return (
    <Legal eyebrow="Terms" title="What this is, and what it is not.">
      <dl className="spec mt-8">
        <div><dt>The registry is signal</dt><dd>Most entries are unverified reports filed by the public. They are published as claims, not as findings, and every surface says which. Nothing here is a certification, an audit, or a warranty about any product.</dd></div>
        <div><dt>Use at your own judgement</dt><dd>The admission gate applies your policy to reported data. The decision, and the consequences of it, are yours.</dd></div>
        <div><dt>Not a price</dt><dd>The exposure calculator does arithmetic on figures you supply. It is not a quote, an offer of insurance, or an actuarial opinion.</dd></div>
        <div><dt>Right of reply</dt><dd>Anyone named may dispute, reply and assert a resolution. A verified owner's dispute suspends an incident from published statistics while it runs, and an undecided dispute resolves in their favour at the deadline.</dd></div>
        <div><dt>Fair use</dt><dd>The public API is open and unauthenticated. Do not use it to file reports you know to be false — that is the one thing that makes a registry worthless to everybody, including you.</dd></div>
        <div><dt>Trademarks</dt><dd>Product and company names are the trademarks of their respective owners. Naming a product in a report is not a claim of affiliation.</dd></div>
      </dl>
    </Legal>
  )
}

export function Changelog() {
  const entries: [string, string[]][] = [
    ["2026-08-04", [
      "The console splits in two: a tenant console and a separate staff surface. They were one app, and the staff half operates the public registry rather than any customer's data.",
      "`adjudicate` and `leads:read` are granted by name only. A tenant-owner wildcard key could previously record verdicts on strangers' incidents and export every captured address.",
      "The admission gate answers about a framework and a model, not only an agent DID — which is what its own page had always asked it.",
      "The exposure calculator takes your reversibility and review coverage. Both figures are yours; agitome supplies no coefficient.",
      "The drop publishes its corpus by quarter, and the dataset download it advertised now serves a file.",
      "A risk badge you can embed as an image, and a sitemap that lists all twenty-two pages rather than two.",
    ]],
    ["2026-08-03", [
      "Privacy policy, terms, a questions page, and a footer that carries a copyright line and trademark attribution.",
      "Open Graph cards, so a link pasted into Slack stops rendering as a bare text row.",
    ]],
  ]
  return (
    <Legal eyebrow="Changelog" title="What changed, and when.">
      <div className="mt-8">
        {entries.map(([date, items]) => (
          <div key={date} style={{ marginBottom: "var(--s7)" }}>
            <p className="eyebrow">{date}</p>
            <ul className="stack mt-3" style={{ paddingLeft: "1.1rem" }}>
              {items.map((i) => <li key={i} className="mt-2">{i}</li>)}
            </ul>
          </div>
        ))}
      </div>
    </Legal>
  )
}

export function ContactPage() {
  return (
    <Shell>
      <div className="band band--bone">
        <div className="wrap wrap--narrow">
          <p className="eyebrow">Contact</p>
          <h1 className="display mt-4 balance">Ask us anything about the registry</h1>
          <p className="lede mt-4">
            The most useful questions come from two people: somebody deciding whether to ship on an
            agent, and somebody whose product has a page here. Both get a reply from a person.
          </p>

          <div className="mt-8">
            <Contact surface="contact" />
          </div>

          <p className="muted mt-8">
            If a page names your product and you want to answer it, you do not need this form —{" "}
            <a href="/tools/vendors">the vendor path</a> gives you a claim, a dispute and a permanent
            statement. This is for everything else.
          </p>
        </div>
      </div>
    </Shell>
  )
}
